import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

/**
 * 参数示例:
 * ```typescript
 * {
 *   "promptText": "A beautiful sunset scene with gentle ocean waves",
 *   "promptImage": "https://example.com/input_image.jpg",
 *   "duration": 5,
 *   "aspectRatio": "16:9",
 *   "resolution": "720p",
 *   "seed": 12345,
 *   "watermark": false
 * }
 * ```
 */
export interface RunwayGen3aTurboI2vParams {
  /**
   * 视频生成提示词（可选），最长1000个字符（UTF-16编码单元计数），详细描述视频中应出现的内容
   * @length 0 - 1000
   */
  promptText?: string;
  /**
   * 输入图像，可以是单个图像URL或包含位置信息的图像数组
   */
  promptImage?: string | string[];
  /**
   * 视频时长（可选），单位：秒。支持: 5（5秒）或 10（10秒）
   * @values 5 | 10
   */
  duration?: 5 | 10;
  /**
   * 视频宽高比（可选），常见选项包括: 16:9（横屏）、9:16（竖屏）、1:1（方形）等
   * @example "16:9"
   */
  aspectRatio?: string;
  /**
   * 视频分辨率（可选），例如: 720p、1080p 等
   * @example "720p"
   */
  resolution?: string;
  /**
   * 随机种子（可选），范围: 0-4294967295。如果未指定，将随机选择。使用相同的seed和其他参数可以生成相似的结果
   * @range 0 - 4294967295
   */
  seed?: number;
  /**
   * 是否添加水印（可选），true表示添加水印，false表示不添加
   */
  watermark?: boolean;
  /**
   * 内容审核设置（可选）
   */
  contentModeration?: {
    /**
     * 公众人物识别阈值：auto（自动）或 low（较低，内容审核系统对包含可识别公众人物的生成内容限制较少）
     * @default "auto"
     * @values auto | low
     */
    publicFigureThreshold?: 'auto' | 'low';
  };
}

export interface RunwayGen3aTurboI2vResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * runway_gen3a_turbo_i2v
 *
 * Runway Gen-3 Alpha Turbo 图像生成视频接口，支持将静态图像转换为动态视频。
 * 
 * Model: gen3a_turbo
 * Features:
 * - 支持图像到视频生成（I2V）
 * - 支持5秒或10秒视频时长
 * - 支持多种宽高比：16:9、9:16、1:1等
 * - 支持不同分辨率：720p、1080p等
 * - 支持首帧和尾帧图像输入
 * - 支持种子值（seed）控制生成结果
 *
 * @example
 * ```typescript
 * import { initSeacloud, runwayGen3aTurboI2v } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await runwayGen3aTurboI2v(
 *   {
 *     "promptText": "A beautiful sunset scene with gentle ocean waves",
 *     "promptImage": "https://example.com/input_image.jpg",
 *     "duration": 5,
 *     "aspectRatio": "16:9",
 *     "resolution": "720p",
 *     "seed": 12345,
 *     "watermark": false
 *   }
 * );
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 RunwayGen3aTurboI2vParams 接口定义
 * @returns 生成的资源列表
 */
export async function runwayGen3aTurboI2v(
  params: RunwayGen3aTurboI2vParams,
): Promise<RunwayGen3aTurboI2vResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 5000,
    maxAttempts: 120,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'runway_gen3a_turbo_i2v',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: RunwayGen3aTurboI2vResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
